import { useCallback, useEffect, useMemo, useState } from 'react';

import type { IncomingAppMessage } from '@/types/appMessage';
import type { MemberJoinAlert } from '@/types/memberJoinAlert';
import { isMemberJoinedClearedMessage, isMemberJoinedMessage } from '@/utils/appMessageGuards';

const msUntil = (expiresAt: string): number | null => {
  const remaining = new Date(expiresAt).getTime() - Date.now();

  return Number.isNaN(remaining) ? null : remaining;
};

const useMemberJoinAlerts = () => {
  const [alerts, setAlerts] = useState<MemberJoinAlert[]>([]);

  const clearAlert = useCallback((collapseId: string) => {
    setAlerts((current) => current.filter((alert) => alert.collapseId !== collapseId));
  }, []);

  const minimizeAlert = useCallback((collapseId: string) => {
    setAlerts((current) =>
      current.map((alert) =>
        alert.collapseId === collapseId ? { ...alert, minimized: true } : alert
      )
    );
  }, []);

  // Move the alert to the front so clicking a specific pill opens that alert
  // rather than whichever happens to be first in the queue.
  const maximizeAlert = useCallback((collapseId: string) => {
    setAlerts((current) => {
      const target = current.find((alert) => alert.collapseId === collapseId);

      if (!target) {
        return current;
      }

      return [
        { ...target, minimized: false },
        ...current.filter((alert) => alert.collapseId !== collapseId)
      ];
    });
  }, []);

  const receiveMessage = useCallback(
    (message: IncomingAppMessage) => {
      const collapseId = message.collapse_id;

      if (!collapseId) {
        return;
      }

      if (isMemberJoinedClearedMessage(message)) {
        clearAlert(collapseId);
        return;
      }

      if (!isMemberJoinedMessage(message)) {
        return;
      }

      // A message redelivered after the appointment ended should not put an
      // alert back on screen.
      const expiresAt = message.expires_at || null;
      const remaining = expiresAt ? msUntil(expiresAt) : null;

      if (remaining !== null && remaining <= 0) {
        return;
      }

      setAlerts((current) => {
        const existing = current.find((alert) => alert.collapseId === collapseId);

        // The backend collapses per appointment, so a second member joining
        // resends the same collapse id with new text. Update in place: hold the
        // queue position, and stay minimized if the counselor minimized it.
        if (existing) {
          return current.map((alert) =>
            alert.collapseId === collapseId
              ? {
                  ...alert,
                  data: message.data,
                  expiresAt,
                  joinUrl: message.action_url || null,
                  text: message.text
                }
              : alert
          );
        }

        return [
          ...current,
          {
            collapseId,
            data: message.data,
            expiresAt,
            joinUrl: message.action_url || null,
            minimized: false,
            receivedAt: Date.now(),
            text: message.text
          }
        ];
      });
    },
    [clearAlert]
  );

  // Timers are derived from each alert's absolute expiry, so rescheduling after
  // an unrelated change (a minimize, say) lands on the same moment.
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    alerts.forEach((alert) => {
      const remaining = alert.expiresAt ? msUntil(alert.expiresAt) : null;

      if (remaining === null) {
        return;
      }

      timers.push(setTimeout(() => clearAlert(alert.collapseId), Math.max(remaining, 0)));
    });

    return () => timers.forEach(clearTimeout);
  }, [alerts, clearAlert]);

  // One alert is shown at a time; the rest wait in the minimized bar.
  const activeAlert = useMemo(() => alerts.find((alert) => !alert.minimized) ?? null, [alerts]);

  return {
    activeAlert,
    alerts,
    clearAlert,
    maximizeAlert,
    minimizeAlert,
    receiveMessage
  };
};

export default useMemberJoinAlerts;
